import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import SafeAvatar from "@/components/SafeAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Edit3, Mail, Phone, MapPin, Bell, BellOff, ShieldCheck,
  LogOut, ChevronRight, Camera, User, Sparkles,
} from "lucide-react";
import OtpDialog from "@/components/OtpDialog";
import DeleteAccountButton from "@/components/DeleteAccountButton";
import { subscribeToPush as subscribePush, unsubscribeFromPush as unsubscribePush } from "@/lib/push";

export default function PartProfile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [city, setCity] = useState(user?.city || "");
  const [postal, setPostal] = useState(user?.postal_code || "");
  const [description, setDescription] = useState(user?.description || "");
  const [avatar, setAvatar] = useState(user?.avatar || "");
  const [pushOn, setPushOn] = useState(!!user?.push_subscription);
  const [otp, setOtp] = useState({ open: false, channel: null });

  useEffect(() => {
    setName(user?.name || "");
    setPhone(user?.phone || "");
    setCity(user?.city || "");
    setPostal(user?.postal_code || "");
    setDescription(user?.description || "");
    setAvatar(user?.avatar || "");
    setPushOn(!!user?.push_subscription);
  }, [user]);

  const save = async () => {
    try {
      await api.patch("/profile/me", {
        name, phone, city, postal_code: postal, description, avatar,
      });
      await refresh();
      setEditing(false);
      toast.success("Profil mis à jour");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const uploadAvatar = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Photo trop lourde (max 3 Mo)"); return; }
    const reader = new FileReader();
    reader.onload = () => setAvatar(reader.result);
    reader.readAsDataURL(file);
  };

  const togglePush = async () => {
    try {
      if (pushOn) { await unsubscribePush(); setPushOn(false); toast.success("Notifications désactivées"); }
      else { await subscribePush(); setPushOn(true); toast.success("Notifications activées"); }
      await refresh();
    } catch (e) { toast.error("Impossible de modifier les notifications"); }
  };

  const verify = (channel) => setOtp({ open: true, channel });

  if (!user) return null;

  return (
    <AppShell role="particulier" title="Mon profil">
      {/* Cover + avatar */}
      <div className="relative rounded-3xl overflow-hidden mb-6" data-testid="part-profile-header">
        <div className="h-28 bg-gradient-to-br from-brand via-brand-hover to-pro" />
        <div className="bg-white pt-10 pb-4 px-5 rounded-b-3xl border-x border-b border-border">
          <div className="absolute top-14 left-5">
            <div className="relative">
              <SafeAvatar
                src={avatar || user.avatar}
                role={user.role}
                name={user.name}
                className="w-20 h-20 rounded-2xl border-4 border-white shadow-lg object-cover"
                data-testid="part-avatar"
              />
              {editing && (
                <label className="absolute -bottom-1 -right-1 h-8 w-8 rounded-xl bg-brand text-white flex items-center justify-center cursor-pointer shadow-md">
                  <Camera className="w-4 h-4" />
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="part-avatar-input" />
                </label>
              )}
            </div>
          </div>
          <div className="flex justify-end">
            {!editing && (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(true)} data-testid="part-edit-btn">
                <Edit3 className="w-3.5 h-3.5 mr-1.5" /> Modifier
              </Button>
            )}
          </div>
          <div className="mt-3">
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl font-heading font-bold text-lg" data-testid="part-name-input" />
            ) : (
              <h2 className="font-heading font-bold text-xl text-ink" data-testid="part-name">{user.name}</h2>
            )}
            <div className="text-xs text-slate2 mt-0.5 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {editing ? (
                <span className="flex gap-1">
                  <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ville" className="rounded-lg h-7 text-xs w-32" data-testid="part-city-input" />
                  <Input value={postal} onChange={(e) => setPostal(e.target.value.replace(/\D/g, "").slice(0, 5))} placeholder="CP" className="rounded-lg h-7 text-xs w-16" data-testid="part-postal-input" />
                </span>
              ) : (
                <span>{[user.postal_code, user.city].filter(Boolean).join(" ") || "Ville à renseigner"}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bloc description / mot pour les artisans */}
      <div className="bg-white rounded-2xl border border-border p-4 mb-4" data-testid="part-about-block">
        <h3 className="text-xs uppercase tracking-wider text-slate2 font-semibold mb-2">À propos de moi</h3>
        {editing ? (
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Quelques mots pour aider les artisans à mieux vous comprendre (optionnel)…" className="rounded-xl" data-testid="part-description-input" maxLength={500} />
        ) : (
          <p className="text-sm text-slate2 leading-relaxed">
            {user.description || <span className="italic text-slate-400">Ajoutez une courte présentation.</span>}
          </p>
        )}
      </div>

      {editing && (
        <div className="flex gap-2 mb-4">
          <Button variant="outline" className="rounded-full flex-1" onClick={() => setEditing(false)} data-testid="part-cancel-btn">Annuler</Button>
          <Button className="rounded-full flex-1 bg-brand hover:bg-brand-hover" onClick={save} data-testid="part-save-btn">Enregistrer</Button>
        </div>
      )}

      {/* Vérifications & coordonnées */}
      <div className="bg-white rounded-2xl border border-border p-4 mb-4">
        <h3 className="text-xs uppercase tracking-wider text-slate2 font-semibold mb-3">Coordonnées & vérifications</h3>
        <div className="space-y-2.5">
          <Row icon={Mail} label="Email" value={user.email} verified={user.verified_email} onVerify={() => verify("email")} testid="part-verify-email" />
          <Row icon={Phone} label="Téléphone" value={editing ? null : user.phone} verified={user.verified_phone} onVerify={() => verify("phone")} testid="part-verify-phone">
            {editing && <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="06 12 34 56 78" className="rounded-lg h-8 text-sm" data-testid="part-phone-input" />}
          </Row>
        </div>
      </div>

      {/* Notifications */}
      <button
        onClick={togglePush}
        className="w-full bg-white rounded-2xl border border-border p-4 flex items-center justify-between mb-4 tap-scale"
        data-testid="part-toggle-push"
      >
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${pushOn ? "bg-brand/10 text-brand" : "bg-bone text-slate2"}`}>
            {pushOn ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div className="text-left">
            <div className="text-sm font-heading font-bold text-ink">Notifications</div>
            <div className="text-[11px] text-slate2">{pushOn ? "Actives — vous serez alerté(e) des devis reçus" : "Désactivées"}</div>
          </div>
        </div>
        <div className={`w-10 h-6 rounded-full relative transition-colors ${pushOn ? "bg-brand" : "bg-slate-200"}`}>
          <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${pushOn ? "left-[18px]" : "left-0.5"}`} />
        </div>
      </button>

      {/* Quick links */}
      <div className="bg-white rounded-2xl border border-border overflow-hidden mb-4">
        <QuickLink icon={User} label="Mes demandes" hint="Suivi et devis reçus" onClick={() => nav("/p/requests")} testid="part-quick-requests" />
        <QuickLink icon={Sparkles} label="Assistant IA" hint="Rédiger ma demande" onClick={() => nav("/p/ai")} testid="part-quick-ai" />
      </div>

      {/* Logout */}
      <button
        onClick={() => { logout(); nav("/onboarding"); }}
        className="w-full mt-2 h-11 rounded-full border border-border text-slate2 text-sm font-semibold tap-scale flex items-center justify-center gap-2"
        data-testid="part-logout-btn"
      >
        <LogOut className="w-4 h-4" /> Se déconnecter
      </button>

      <OtpDialog
        open={otp.open}
        onOpenChange={(v) => setOtp((s) => ({ ...s, open: v }))}
        channel={otp.channel}
        onVerified={refresh}
      />

      <DeleteAccountButton testId="part-delete-account-btn" />
    </AppShell>
  );
}

function Row({ icon: Icon, label, value, verified, onVerify, testid, children }) {
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testid}>
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="h-8 w-8 rounded-xl bg-bone flex items-center justify-center text-slate2 shrink-0">
          <Icon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate2">{label}</div>
          {children ? children : <div className="text-sm text-ink truncate">{value || "—"}</div>}
        </div>
      </div>
      {verified ? (
        <span className="text-[10px] font-bold uppercase tracking-widest text-forest bg-forest/10 rounded-full px-2 py-1 flex items-center gap-1 shrink-0" data-testid={`${testid}-verified`}>
          <ShieldCheck className="w-3 h-3" /> Vérifié
        </span>
      ) : (value ? (
        <button onClick={onVerify} className="text-[10px] font-bold uppercase tracking-widest text-brand bg-brand/10 rounded-full px-2 py-1 tap-scale shrink-0" data-testid={`${testid}-btn`}>
          Vérifier
        </button>
      ) : null)}
    </div>
  );
}

function QuickLink({ icon: Icon, label, hint, onClick, testid }) {
  return (
    <button onClick={onClick} className="w-full flex items-center justify-between px-4 py-3.5 border-b border-border last:border-0 tap-scale" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className="h-9 w-9 rounded-xl bg-brand/10 text-brand flex items-center justify-center"><Icon className="w-4 h-4" /></span>
        <div className="text-left">
          <div className="text-sm font-heading font-bold text-ink">{label}</div>
          <div className="text-[11px] text-slate2">{hint}</div>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-400" />
    </button>
  );
}
